import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Shield, Zap, Sparkles, Target } from "lucide-react";

const values = [
  {
    icon: <Sparkles size={32} />,
    title: "Cinematic Vision",
    desc: "We approach every digital interaction as a scene from a masterpiece. Every pixel is a choice, every animation a beat.",
    image: "/images/team/ethos_cinematic_vision.png"
  },
  {
    icon: <Zap size={32} />,
    title: "Agile Precision",
    desc: "Speed without sacrifice. We build fast, but we build for forever. High performance is our baseline.",
    image: "/images/team/ethos_agile_precision.png"
  },
  {
    icon: <Shield size={32} />,
    title: "Deep Integrity",
    desc: "Security and trust are woven into the very fabric of our code. We protect your vision like our own.",
    image: "/images/team/ethos_deep_integrity.png"
  },
  {
    icon: <Target size={32} />,
    title: "Audacious Impact",
    desc: "We don't just follow trends; we define the new standard. Audacity is in our DNA.",
    image: "/images/team/ethos_audacious_impact.png"
  }
];

const TeamValues = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <>
      {/* Desktop Version */}
      <section id="ethos" ref={containerRef} className="team-values-cinematic hidden lg:block">
        <div className="sticky-wrapper">
          {/* Dynamic Backgrounds */}
          <div className="value-backgrounds">
            {values.map((v, i) => (
              <ValueBackgroundItem 
                key={i}
                image={v.image}
                index={i}
                progress={smoothProgress}
                total={values.length}
              />
            ))}
            <div className="value-overlay"></div>
          </div>

          {/* Content Container */}
          <div className="container">
            <div className="value-content-grid">
              <div className="value-left">
                <ValueKicker index="04" label="OUR ETHOS" />
                <h2 className="value-main-title">
                  THE PRINCIPLES <br />
                  <span className="italic gold-text">THAT DEFINE US</span>
                </h2>
                
                <div className="value-nav">
                  {values.map((v, i) => (
                    <ValueIndicator 
                      key={i}
                      index={i}
                      title={v.title}
                      progress={smoothProgress}
                      total={values.length}
                    />
                  ))}
                </div>
              </div>

              <div className="value-right">
                {values.map((v, i) => (
                  <ValueContentItem 
                    key={i}
                    index={i}
                    icon={v.icon}
                    title={v.title}
                    desc={v.desc}
                    progress={smoothProgress}
                    total={values.length}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Version - Sticky Stacking like /about */}
      <section className="lg:hidden bg-[#0a0908] py-24 px-4 overflow-hidden">
        <div className="container">
          <div className="mb-16">
            <ValueKicker index="04" label="OUR ETHOS" />
            <h2 className="mt-6 font-display text-4xl leading-[1.1] text-white">
              THE PRINCIPLES <br />
              <span className="italic gold-text">THAT DEFINE US</span>
            </h2>
          </div>

          <div className="flex flex-col gap-[10vh]">
            {values.map((v, i) => (
              <MobileValueCard 
                key={i} 
                value={v} 
                index={i} 
                total={values.length} 
              />
            ))}
          </div>
          
          <div className="h-[20vh]" />
        </div>
      </section>
    </>
  );
};

const MobileValueCard = ({ value, index, total }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0.5, 0.8], [1, 0.92]);
  const opacity = useTransform(scrollYProgress, [0.5, 0.8], [1, 0.6]);
  const overlayOpacity = useTransform(scrollYProgress, [0.5, 0.8], [0, 0.6]);
  const baseRotation = (index % 2 === 0 ? -1 : 1) * (1 + Math.random() * 2);

  return (
    <div
      ref={cardRef}
      className="sticky w-full"
      style={{ 
        top: `${120 + index * 20}px`,
        zIndex: index 
      }}
    >
      <motion.div
        style={{ 
          scale, 
          opacity,
          rotate: baseRotation
        }}
        className="relative aspect-[3/4] rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_-20px_60px_rgba(0,0,0,0.8)] bg-[#0a0908]"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${value.image})`, 
            opacity: 0.6 
          }}
        />
        <motion.div 
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        <div className="absolute inset-0 p-8 flex flex-col justify-end">
          <div className="value-icon-box mb-6 !w-12 !h-12 !border-white/20">
            {React.cloneElement(value.icon, { size: 24 })}
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <span className="font-impact text-sm tracking-[0.4em] text-[var(--accentColor)] uppercase">0{index + 1}</span>
            <div className="w-12 h-px bg-[var(--accentColor)]" />
          </div>
          
          <h3 className="font-display text-3xl text-white mb-4 leading-tight">
            {value.title}
          </h3>
          
          <p className="text-white/60 text-[15px] leading-relaxed font-light">
            {value.desc}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const ValueBackgroundItem = ({ image, index, progress, total }) => {
  const start = index / total;
  const end = (index + 1) / total;
  const opacity = useTransform(progress, [start - 0.1, start, end, end + 0.1], [0, 0.7, 0.7, 0]);
  const scale = useTransform(progress, [start, end], [1.1, 1]);

  return (
    <motion.div 
      style={{ opacity, scale, backgroundImage: `url(${image})` }}
      className="value-bg-img"
    />
  );
};

const ValueIndicator = ({ index, title, progress, total }) => {
  const start = index / total;
  const end = (index + 1) / total;
  const opacity = useTransform(progress, [start - 0.05, start, end, end + 0.05], [0.3, 1, 1, 0.3]);
  const x = useTransform(progress, [start - 0.05, start], [0, 20]);

  return (
    <motion.div style={{ opacity, x }} className="value-indicator">
      <span className="indicator-number">0{index + 1}</span>
      <span className="indicator-title">{title}</span>
    </motion.div>
  );
};

const ValueContentItem = ({ index, icon, title, desc, progress, total }) => {
  const start = index / total;
  const end = (index + 1) / total;
  const opacity = useTransform(progress, [start - 0.05, start, end - 0.05, end], [0, 1, 1, 0]);
  const y = useTransform(progress, [start, end], [50, -50]);

  return (
    <motion.div style={{ opacity, y }} className="value-content-item">
      <div className="value-icon-box">{icon}</div>
      <h3 className="value-step-title">{title}</h3>
      <p className="value-step-desc">{desc}</p>
    </motion.div>
  );
};

const ValueKicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--accentColor)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-white/40 uppercase">{label}</span>
  </div>
);

export default TeamValues;
